import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBESSStore } from '../../app/store';
import { useCurrentProject, useProjectCompleteness } from '../../hooks/useProjectData';
import { clsx } from 'clsx';
import { CheckCircle2, Database, GitCompare, Battery, DollarSign } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

interface Step {
  number: 1 | 2 | 3 | 4;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
}

export function StepProgress() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentStep, canProceedToStep2, canProceedToStep3, optimization, goToStep} = useBESSStore();

  const steps: Step[] = [
    {
      number: 1,
      title: t('steps.dataInput'),
      description: 'Upload consumption, bill, and price data',
      icon: <Database className="w-5 h-5" />,
      route: '/',
    },
    {
      number: 2,
      title: t('steps.optimization'),
      description: 'Configure BESS and financial parameters',
      icon: <GitCompare className="w-5 h-5" />,
      route: '/optimization/analysis',
    },
    {
      number: 3,
      title: t('steps.financialConfig'),
      description: 'View optimal battery configurations',
      icon: <Battery className="w-5 h-5" />,
      route: '/optimization/results',
    },
    {
      number: 4,
      title: t('steps.financialAnalysis'),
      description: 'Detailed financial metrics and ROI',
      icon: <DollarSign className="w-5 h-5" />,
      route: '/results/financial',
    },
  ];

  // Get current project and data completeness status
  const { data: currentProject } = useCurrentProject();
  const { hasAllData, hasAlignedData } = useProjectCompleteness(currentProject?.id);

  // Check if optimization results exist (in store OR backend)
  const [hasOptimizationResults, setHasOptimizationResults] = useState(false);

  useEffect(() => {
    // If optimization is in store, we're good
    if (optimization?.configurations && optimization.configurations.length > 0) {
      setHasOptimizationResults(true);
      return;
    }

    // Otherwise, check backend for saved results
    fetch(`${API_BASE_URL}/optimization/get-results`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data?.all_configurations?.length > 0) {
          setHasOptimizationResults(true);
        } else {
          setHasOptimizationResults(false);
        }
      })
      .catch(() => setHasOptimizationResults(false));
  }, [optimization]);

  // Update current step based on route
  useEffect(() => {
    const path = location.pathname;

    if (path === '/' || path.startsWith('/data-input')) {
      goToStep(1);
    } else if (path === '/optimization/configure' || path === '/optimization/analysis' || path === '/optimization') {
      goToStep(2);
    } else if (path.startsWith('/optimization/results')) {
      goToStep(3);
    } else if (path.startsWith('/results/financial') || path.startsWith('/financial')) {
      goToStep(4);
    }
  }, [location.pathname, goToStep]);

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'active';
    // Step 2 requires all data to be uploaded
    if (stepNumber === 2 && !hasAllData) return 'disabled';
    // Step 3 requires aligned data
    if (stepNumber === 3 && !hasAlignedData) return 'disabled';
    // Step 4 requires optimization results (in store OR backend)
    if (stepNumber === 4 && !hasOptimizationResults) return 'disabled';
    return 'pending';
  };

  const handleStepClick = (step: Step) => {
    const status = getStepStatus(step.number);
    if (status !== 'disabled') {
      navigate(step.route);
    }
  };

  return (
    <div className="bg-white border-b border-swiss-mist">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const status = getStepStatus(step.number);
            const isLast = index === steps.length - 1;

            return (
              <div key={step.number} className="flex items-center flex-1">
                <button
                  onClick={() => handleStepClick(step)}
                  disabled={status === 'disabled'}
                  className={clsx(
                    'flex items-center focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 rounded-lg p-2 -m-2 transition-all',
                    {
                      'cursor-pointer hover:bg-swiss-mist': status !== 'disabled',
                      'cursor-not-allowed': status === 'disabled',
                    }
                  )}
                >
                  <div
                    className={clsx(
                      'relative flex items-center justify-center w-12 h-12 rounded-full transition-all',
                      {
                        'bg-brand-primary text-white': status === 'completed' || status === 'active',
                        'bg-swiss-pearl text-swiss-stone': status === 'pending',
                        'bg-swiss-mist text-swiss-stone opacity-50': status === 'disabled',
                      }
                    )}
                  >
                    {status === 'completed' ? (
                      <CheckCircle2 className="w-6 h-6" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  
                  <div className="ml-3 text-left">
                    <p
                      className={clsx(
                        'text-sm font-medium',
                        {
                          'text-swiss-charcoal': status === 'active' || status === 'completed',
                          'text-swiss-stone': status === 'pending',
                          'text-swiss-stone opacity-50': status === 'disabled',
                        }
                      )}
                    >
                      {t('common.step')} {step.number}
                    </p>
                    <p
                      className={clsx(
                        'text-xs',
                        {
                          'text-brand-primary font-medium': status === 'active',
                          'text-swiss-graphite': status === 'completed',
                          'text-swiss-stone': status === 'pending',
                          'text-swiss-stone opacity-50': status === 'disabled',
                        }
                      )}
                    >
                      {step.title}
                    </p>
                  </div>
                </button>
                
                {!isLast && (
                  <div className="flex-1 mx-4">
                    <div
                      className={clsx(
                        'h-0.5 transition-all',
                        {
                          'bg-brand-primary': status === 'completed',
                          'bg-swiss-mist': status !== 'completed',
                        }
                      )}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}